import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { deleteBannerRotator } from 'cityads-v3/reducers/bannerRotator'
import { BannerRotator } from 'cityads-v3/types/bannerRotator'
import { useFormState } from 'cityads-v3/hooks/form'
import { useCheckPermission } from 'cityads-v3/hooks/permissions'

import CircularProgress from '@mui/material/CircularProgress'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/EditOutlined'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVertOutlined'

type ActionsButtonProps = {
  id: BannerRotator['id'],
  onDelete?: () => void
}

export default function ActionsButton({ id, onDelete }: ActionsButtonProps): JSX.Element {
  const { t } = useTranslation(['main'])
  const dispatch = useDispatch()
  const [anchorEl, setAnchorEl] = useState(null)
  const removeState = useFormState();
  const checkPermission = useCheckPermission()

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [setAnchorEl])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [setAnchorEl])

  const handleDelete = useCallback((id: number) => async () => {
    removeState.setIsSending(true)
    await dispatch(deleteBannerRotator(id))
    handleClose()
    removeState.setSucceeded()
    onDelete?.()
  }, [dispatch, handleClose, removeState, onDelete])

  if (!checkPermission('edit_banner_rotator')) {
    return null;
  }

  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
      >
        <MenuItem component={Link} to={`/publisher/tools/bannerRotator/edit/${id}`}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>{t('main:buttons.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete(id)}>
          <ListItemIcon>
            {removeState.isSending ? <CircularProgress size={24} color="secondary" /> : <DeleteOutlinedIcon color="secondary" />}
          </ListItemIcon>
          <ListItemText
            slotProps={{
              primary: {
                color: 'secondary',
              },
            }}
          >{t('main:buttons.delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}
